// @ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import { useLanguageContext } from "../context/languageContext";

/**
 * Sticky category bar.
 *
 * Selecting a tab scrolls to that category, and scrolling the page selects the
 * tab back - so the bar always shows where the guest is. The strip keeps the
 * active tab in view, which matters once there are more than a few categories
 * on a phone.
 */
export const CategoryTabs = ({ categories, activeId, onSelect, className = "" }) => {
    const { t } = useLanguageContext();
    const stripRef = useRef(null);
    const barRef = useRef(null);

    // A tap selects its tab straight away rather than waiting for the smooth scroll
    // to arrive; the scroll tracker takes back over once it catches up.
    const [tapped, setTapped] = useState(null); // { id, seenAt } - seenAt = activeId when tapped
    const currentId = tapped ? tapped.id : activeId;

    useEffect(() => {
        if (!tapped) return undefined;

        // The scroll landed on the tapped category: hand back cleanly.
        if (String(tapped.id) === String(activeId)) {
            setTapped(null);
            return undefined;
        }

        // Otherwise only release the tap once tracking has demonstrably moved on
        // (the guest scrolled somewhere else). If tracking never reports anything,
        // the tab stays where it was tapped rather than snapping back.
        if (String(activeId) !== String(tapped.seenAt)) {
            const timer = window.setTimeout(() => setTapped(null), 900);
            return () => window.clearTimeout(timer);
        }
        return undefined;
    }, [tapped, activeId]);

    useEffect(() => {
        const strip = stripRef.current;
        if (!strip || currentId == null) return;
        const chip = strip.querySelector(`[data-tab-id="${currentId}"]`);
        if (!chip) return;

        // Centre the active chip by moving the strip only - never the page.
        const target = chip.offsetLeft - strip.clientWidth / 2 + chip.clientWidth / 2;
        const left = Math.max(0, Math.min(target, strip.scrollWidth - strip.clientWidth));
        if (Math.abs(strip.scrollLeft - left) > 4) {
            strip.scrollTo({ left, behavior: "smooth" });
        }
    }, [currentId]);

    // Publish this bar's height too, so section headings can be scrolled clear of
    // both it and the header.
    useEffect(() => {
        const publish = () => {
            const h = barRef.current?.offsetHeight;
            if (h) document.documentElement.style.setProperty("--app-tabbar-h", `${h}px`);
        };
        publish();
        window.addEventListener("resize", publish);
        return () => window.removeEventListener("resize", publish);
    }, [categories]);

    if (!categories || categories.length === 0) return null;

    return (
        <div
            ref={barRef}
            style={{ top: "var(--app-header-h, 64px)" }}
            className={`sticky z-30 bg-white/95 backdrop-blur-xl border-b border-slate-100 shadow-[0_6px_16px_-14px_rgba(0,0,0,0.35)] ${className}`}
        >
            <div ref={stripRef} className="overflow-x-auto hide-scrollbar scroll-smooth">
                <div className="flex items-center gap-1.5 px-4 py-2.5">
                    {categories.map((category) => {
                        const isActive = String(category.id) === String(currentId);
                        return (
                            <button
                                key={category.id}
                                data-tab-id={category.id}
                                onClick={() => { setTapped({ id: category.id, seenAt: activeId }); onSelect(category); }}
                                aria-current={isActive ? "true" : undefined}
                                className={`flex-shrink-0 whitespace-nowrap rounded-full px-3.5 py-2 text-[11px] font-bold tracking-[0.06em] transition-all duration-200 ${isActive
                                    ? "bg-brand text-white shadow-[0_6px_14px_-6px_rgba(0,102,52,0.7)]"
                                    : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                                    }`}
                            >
                                {t(`categories.${category.categoryName}`, category.categoryName)}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default CategoryTabs;
